import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import { useGenerateChangelogDraft } from "@/hooks/use-changelog";
import type { GenerateChangelogDraftResult } from "@/types/changelog";

const MAX_DESCRIPTION_LENGTH = 2000;

interface ChangelogAiDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDraftGenerated: (draft: GenerateChangelogDraftResult) => void;
}

export function ChangelogAiDraftDialog({
  open,
  onOpenChange,
  onDraftGenerated,
}: ChangelogAiDraftDialogProps) {
  const [description, setDescription] = useState("");
  const generateMutation = useGenerateChangelogDraft();
  const { reset } = generateMutation;

  useEffect(() => {
    if (open) {
      setDescription("");
      reset();
    }
  }, [open, reset]);

  const handleGenerate = async () => {
    const trimmed = description.trim();
    if (!trimmed) return;

    try {
      const draft = await generateMutation.mutateAsync({
        description: trimmed,
      });
      onDraftGenerated(draft);
      onOpenChange(false);
    } catch {
      // Error handled by mutation onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI 초안 생성
          </DialogTitle>
          <DialogDescription>
            변경사항을 간단히 설명하면 AI가 체인지로그 초안을 작성해 드립니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Textarea
            aria-label="변경사항 설명"
            placeholder="예: 대시보드에 날짜 필터가 추가되었고, 알림 설정 화면이 더 깔끔해졌습니다"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleGenerate();
              }
            }}
            maxLength={MAX_DESCRIPTION_LENGTH}
            rows={5}
            className="resize-none break-all"
          />
          {description.length > MAX_DESCRIPTION_LENGTH * 0.8 && (
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generateMutation.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!description.trim() || generateMutation.isPending}
            onClick={handleGenerate}
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                생성 중...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                AI 초안 생성
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
